import { useCallback, useEffect, useRef, useState } from 'react';
import { token } from 'utils';
import { getSocket } from 'utils/socketManager';
import { formService } from 'store/form/form.service';

/**
 * Socket event names — must match `FormAiSocketEvents` in
 * @therapylake/package/form-builder/ai. Duplicated rather than imported
 * because `@therapylake/package/types` is not emitted for the client build
 * (see the note on MAX_AUTHORIZATION_ID_LENGTH in utils/constants.js).
 */
export const FormAiSocketEvents = {
    OUTLINE: 'form-ai:outline',
    SECTION: 'form-ai:section',
    DONE: 'form-ai:done',
    ERROR: 'form-ai:error',
};

export const generationPhases = {
    PENDING: 'PENDING',
    OUTLINING: 'OUTLINING',
    GENERATING: 'GENERATING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
};

/**
 * Drives the AI generation progress modal.
 *
 * Two sources feed the same state, on purpose. The socket is the fast path —
 * it is what makes sections appear the moment they land. The status endpoint
 * is the truthful one: it is read once on mount so a refresh mid-run, or a
 * socket that dropped and reconnected, recovers instead of showing an empty
 * checklist for a run that is actually half done. The backend writes every
 * section to the database before announcing it, so the two can never disagree
 * in a way that loses work.
 *
 * `onSection` receives each finished section so the builder behind the modal
 * can splice it in live.
 */
export const useFormAiGeneration = ({ formId, conversationId, enabled, onSection, onDone }) => {
    const [phase, setPhase] = useState(generationPhases.PENDING);
    const [sections, setSections] = useState([]);
    const [completedIds, setCompletedIds] = useState([]);
    const [error, setError] = useState(null);

    // Held in refs so the socket effect never re-subscribes when the parent
    // re-renders with new closures — re-subscribing mid-run drops events.
    const onSectionRef = useRef(onSection);
    onSectionRef.current = onSection;
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;

    const markComplete = useCallback((sectionId) => {
        setCompletedIds((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]));
    }, []);

    /* ── Fast path: live events ── */
    useEffect(() => {
        if (!enabled || !formId) return undefined;

        const socket = getSocket(token());
        if (!socket) return undefined;

        const isMine = (payload) => payload?.formId === formId;

        const handleOutline = (payload) => {
            if (!isMine(payload)) return;
            setSections(payload.sections || []);
            setPhase(generationPhases.GENERATING);
        };

        const handleSection = (payload) => {
            if (!isMine(payload)) return;
            markComplete(payload.sectionId);
            if (payload.section && onSectionRef.current) onSectionRef.current(payload.section, payload.index);
        };

        const handleDone = (payload) => {
            if (!isMine(payload)) return;
            setPhase(generationPhases.COMPLETED);
            if (onDoneRef.current) onDoneRef.current();
        };

        const handleError = (payload) => {
            if (!isMine(payload)) return;
            // A payload carrying a sectionId is one section giving up, not the
            // run — the others are still going, so keep the modal alive and
            // let the failed row stay unchecked.
            if (payload.sectionId) return;
            setError(payload.message || 'Generation failed');
            setPhase(generationPhases.FAILED);
        };

        socket.on(FormAiSocketEvents.OUTLINE, handleOutline);
        socket.on(FormAiSocketEvents.SECTION, handleSection);
        socket.on(FormAiSocketEvents.DONE, handleDone);
        socket.on(FormAiSocketEvents.ERROR, handleError);

        return () => {
            socket.off(FormAiSocketEvents.OUTLINE, handleOutline);
            socket.off(FormAiSocketEvents.SECTION, handleSection);
            socket.off(FormAiSocketEvents.DONE, handleDone);
            socket.off(FormAiSocketEvents.ERROR, handleError);
        };
    }, [enabled, formId, markComplete]);

    /* ── Truthful path: where the run actually is ── */
    useEffect(() => {
        if (!enabled || !formId) return;

        let cancelled = false;
        formService
            .getAiGenerationStatusService(formId)
            .then(({ data }) => {
                if (cancelled || !data?.status) return;
                setPhase(data.status);
                if (data.error) setError(data.error);
                // Names only — the server stores the plan, not the section ids,
                // so rows recovered this way are keyed by index until the live
                // outline event replaces them.
                setSections((prev) =>
                    prev.length ? prev : (data.plannedSections || []).map((name, index) => ({ id: `planned-${index}`, name })),
                );
                setCompletedIds((prev) =>
                    prev.length ? prev : Array.from({ length: data.completedSections || 0 }, (_, i) => `planned-${i}`),
                );
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [enabled, formId, conversationId]);

    const total = sections.length;
    const completed = completedIds.length;
    // Reserve a slice of the bar for the outline pass, so the modal is visibly
    // working during the seconds before there is anything to count.
    const percent = !total ? (phase === generationPhases.PENDING ? 5 : 15) : Math.round((completed / total) * 100);

    return { phase, sections, completedIds, completed, total, percent, error };
};
